import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Spinner
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Lottie from "lottie-react";
import { toast } from "react-toastify";

import infiniteLoop from "../../assets/img/animated-icons/infinite-loop.json";
import dataTableBgImgLeft from "../../assets/img/illustrations/corner-1.png";
import LogoOne from "../../assets/images/logo.png";

import axiosInstance from "../../axiosSetup";
import AuthCardLayout from "../../layouts/AuthCardLayout";

interface AppOptions {
  name: string;
  desc: string;
  url: string;
}

const Commonapp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [appOptions, setAppOptions] = useState<AppOptions[]>([]);
  const [email, setEmail] = useState<string>();
  const [isAdmin, setIsAdmin] = useState(false);

  const [showImpersonation, setShowImpersonation] = useState(false);
  const [impersonateEmail, setImpersonateEmail] = useState("");
  const [isImpersonating, setIsImpersonating] = useState(false);

  const [userLoaded, setUserLoaded] = useState(false);
  const [appsLoaded, setAppsLoaded] = useState(false);

  const APP_PATH_PREFIX = process.env.REACT_APP_APP_PATH_PREFIX || "";

  /* ----------------------------------------
     Fetch user details
  ---------------------------------------- */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axiosInstance.post(
          "api/user-management/v1/users/getLoginUserDetails"
        );
        setEmail(res.data.email);

        if (res.data.isAdmin) {
          setIsAdmin(true);
          setShowImpersonation(true);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setUserLoaded(true);
      }
    };

    fetchUser();
  }, []);

  /* ----------------------------------------
     Fetch app list
  ---------------------------------------- */
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_GATEWAY_HOST}/api/user-management/v1/app/appList`
        );
        setAppOptions(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setAppsLoaded(true);
      }
    };

    fetchApps();
  }, []);

  /* ----------------------------------------
     Redirect to /home (ONLY after load)
  ---------------------------------------- */
  useEffect(() => {
    if (!userLoaded || !appsLoaded) return;

		if (
			appOptions.length === 0 &&
			(!isAdmin || !showImpersonation)
			) {
		  navigate(`${APP_PATH_PREFIX}/home`, { replace: true });
		}
  }, [userLoaded, appsLoaded, appOptions, isAdmin, showImpersonation, navigate]);

  /* ----------------------------------------
     Auto redirect if only one app
  ---------------------------------------- */
  useEffect(() => {
    if (!userLoaded || !appsLoaded) return;

    if (
      appOptions.length === 1 &&
      !(isAdmin && showImpersonation)
    ) {
      window.location.href = `${APP_PATH_PREFIX}${appOptions[0].url}`;
    }
  }, [userLoaded, appsLoaded, appOptions, isAdmin, showImpersonation]);

  /* ----------------------------------------
     Impersonation submit
  ---------------------------------------- */
const handleImpersonationSubmit = async () => {
  setIsImpersonating(true);

  try {
    const res = await axiosInstance.post(
      `${process.env.REACT_APP_API_GATEWAY_HOST}/auth/admin/impersonate`,
      { customerEmail: impersonateEmail }
    );
    window.location.reload();

    } catch (err: unknown) {
      const axiosErr = err as import("axios").AxiosError<any>;
      toast.error(axiosErr.response?.data?.message || "Impersonation failed");
      setIsImpersonating(false);
    }
};

  /* ----------------------------------------
     UI
  ---------------------------------------- */
  return (
    <AuthCardLayout>
      <Card
        className="bg-card bg-holder"
        style={{
          backgroundImage: `url(${dataTableBgImgLeft})`,
          backgroundSize: "cover",
          backgroundPosition: "40px center",
          paddingTop: "40px",
        }}
      >
        <img src={LogoOne} className="logoWidth pb-4" alt="logo" />

				<Card.Body>
				  <h3 className="text-center mb-2">Welcome to ODeX</h3>

				  {/* Show email only when loaded */}
				  {userLoaded && <h6 className="text-center mb-4">{email}</h6>}

				  {/* GLOBAL LOADING BLOCK */}
				  {(!userLoaded || !appsLoaded) ? (
				    <div className="text-center">
				      <Lottie
				        animationData={infiniteLoop}
				        loop
				        style={{ height: "120px" }}
				      />
				    </div>
				  ) : (
				    <>
				      {/* -------- Inline Impersonation -------- */}
				      {isAdmin && showImpersonation && (
				        <>
				          <h5 className="text-center mb-3">Admin Impersonation</h5>

				          <Form.Group className="mb-3 w-75 mx-auto">
				            <Form.Label>Customer Email</Form.Label>
				            <Form.Control
				              type="email"
				              value={impersonateEmail}
				              onChange={(e) => setImpersonateEmail(e.target.value)}
				              placeholder="Enter customer email"
				            />
				          </Form.Group>

				          <div className="d-flex justify-content-center mt-2">
				            <Button
				              variant="primary"
				              className="mb-2 me-2"
				              style={{ width: "30%", borderRadius: "50px" }}
				              onClick={() => setShowImpersonation(false)}
				            >
				              Cancel
				            </Button>
				            <Button
				              variant="primary"
				              className="mb-2 me-2"
				              style={{ width: "30%", borderRadius: "50px" }}
				              disabled={!impersonateEmail || isImpersonating}
				              onClick={handleImpersonationSubmit}
				            >
				              {isImpersonating
				                ? <Spinner animation="border" size="sm" />
				                : "Impersonate"}
				            </Button>
				          </div>
				        </>
				      )}

				      {/* -------- App list -------- */}
               
				      {appOptions.length > 1 && !showImpersonation && (
                
				        <>
				          <p className="text-center mb-4">
				            Choose an application to continue
				          </p>

				          <div className="d-flex flex-column align-items-center">
                    
				            {appOptions.map((app, idx) => (
				              <Button
				                key={idx}
				                className="mb-2"
				                style={{ width: "42%", borderRadius: "50px" }}
				                onClick={() => {
				                  if (!app.url.startsWith("/")) {
				                    window.location.href = `${app.url}`;
				                  }else{
                            window.location.replace(`${APP_PATH_PREFIX}${app.url}`);
                          }
				                }}
				              >
				                {app.desc}
				              </Button>
				            ))}
				          </div>
				        </>
				      )}
				    </>
				  )}
				</Card.Body>

        <div className="text-center fs--1 mb-3">
          © 2018 – {new Date().getFullYear()} EA Technologies. All rights reserved
        </div>
      </Card>
    </AuthCardLayout>
  );
};

export default Commonapp;
